import { useCallback } from "react";
import { useQuery, useIsFetching, useQueryClient } from "@tanstack/react-query";
import useDecodeToken from "../../hooks/useDecodeToken";

import {
  getDeliveryInformation,
  getOrderForReturnByManualEntry,
} from "../../services/customerService";

import { getReturnPermissions } from "../../services/notificationService";

const QUERY_KEYS = {
  ALL_DEALER_INFORMATION: ["dealerInformation"],
  ALL_Customer_RETURN_PERMISSION: ["customerReturnPermission"],
  ALL_ORDER_FOR_RETURN: ["orderForReturn"],

  DEALER_INFORMATION: (data) => ["dealerInformation", data],
  ORDER_FOR_RETURN: (data) => ["orderForReturn", data],
  CUSTOMER_RETURN_PERMISSION: (data) => ["customerReturnPermission", data],
};

/**
 * @param {{invoiceNumber: string, startDate: Date, endDate: Date}} data
 */

export const useGetDealersInfo = (data) =>
  useQuery({
    queryKey: QUERY_KEYS.DEALER_INFORMATION(data),
    queryFn: () => getDeliveryInformation(data),
    enabled: !!data,
    cacheTime: 0, // 0 seconds, The cache time is the time after which the query will be garbage collected
    staleTime: 0, // 0 seconds, The stale time is the time after which the query will be refetched
  });

/**
 * @param {{invoiceNumber: string, startDate: Date, endDate: Date, dealerId: number}} data
 */

export const useGetPotentialOrdersForReturn = (data) =>
  useQuery({
    queryKey: QUERY_KEYS.ORDER_FOR_RETURN(data),
    queryFn: () => getOrderForReturnByManualEntry(data),
    enabled: !!data,
    cacheTime: 0, // 0 seconds, The cache time is the time after which the query will be garbage collected
    staleTime: 0, // 0 seconds, The stale time is the time after which the query will be refetched
  });

/**
 * @param {{clientId: number, customerId: number}} data
 */

export const useGetReturnPermission = (data) => {
  const [decodedToken] = useDecodeToken();
  const { CustomerId } = decodedToken || {};

  return useQuery({
    queryKey: QUERY_KEYS.CUSTOMER_RETURN_PERMISSION({
      clientId: data,
      customerId: CustomerId,
    }),
    queryFn: () =>
      getReturnPermissions({ clientId: data, customerId: CustomerId }),
    enabled: !!CustomerId && !!data,
    cacheTime: 0, // 0 seconds, The cache time is the time after which the query will be garbage collected
    staleTime: 0, // 0 seconds, The stale time is the time after which the query will be refetched
  });
};

export const useIsFetchingDealerInfo = () =>
  useIsFetching({
    predicate: (query) => query.queryKey.includes("dealerInformation"),
  });

export const useQueryClientInvalidation = () => {
  const queryClient = useQueryClient();
  const [decodedToken] = useDecodeToken();
  const { CustomerId } = decodedToken || {};

  const invalidateQueries = useCallback(
    (data) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.ORDER_FOR_RETURN(data),
      });

      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.CUSTOMER_RETURN_PERMISSION({
          clientId: data?.dealerId,
          customerId: CustomerId,
        }),
      });
    },
    [queryClient, CustomerId]
  );

  return invalidateQueries;
};
